<template>
  <LoadingIcon v-if="isLoading" size="15px" />
  <section v-else-if="!isLoading && categories.length" class="categories-grid-wrapper">
    <section class="categories-grid">
      <CategoryCardDataType
        v-for="category in categories"
        :key="category.id"
        :data="category"
        :icon="category.icon"
        @onSelectCategory="emits('onSelectCategory', category)"
      />
    </section>
    <button
      class="btn btn-primary button-download"
      @click="downloadCSVWithHealthKitData"
      :disabled="isDownloadingCSV"
    >
      <div v-if="isDownloadingCSV" class="spinner-circular"></div>
      <span v-if="!isDownloadingCSV">Download CSV</span>
      <span v-else>Downloading...</span>
    </button>
  </section>
  <div v-else>No data found</div>
</template>

<script lang="js" setup>
import { computed, onMounted, ref, inject } from 'vue';
import { getHealthKitData, getAvailableCategories } from '../services/healthCategories.services';
import {
  HK_CATEGORIES,
  COLUMNS_CSV_WITH_HEALTH_KIT_DATA,
  HK_CATEGORIES_SLEEP_ANALYSIS,
} from '../utils/HealthKit';
import { generateCSVFile } from '@/common/utils/generateCSVFile';
import { formatDate } from '@/common/utils/formDate';
import CategoryCardDataType from '../molecules/CategoryCardDataType.vue';
import { toast } from 'vue3-toastify';
import LoadingIcon from '@/components/loading';

const emits = defineEmits(['onSelectCategory']);

const availableCodes = ref([]);
const isLoading = ref(false);
const isDownloadingCSV = ref(false);

const collectedFormData = inject('collectedFormData');

function createDataTypeMap(categories) {
  const map = new Map();

  Object.entries(categories).forEach(([categoryName, categoryData]) => {
    categoryData.dataType.forEach((dataType) => {
      map.set(dataType.key, {
        categoryName,
        categoryLogo: categoryData.logo,
        dataType,
      });
    });
  });

  return map;
}

function addToCategoryMap(categoryMap, categoryName, categoryLogo, dataType) {
  if (!categoryMap.has(categoryName)) {
    categoryMap.set(categoryName, {
      name: categoryName,
      logo: categoryLogo,
      dataTypes: [],
    });
  }

  categoryMap.get(categoryName).dataTypes.push({
    key: dataType.key,
    name: dataType.name,
    color: dataType.color,
    chartType: dataType.typeChart,
  });
}

const dataTypeMap = createDataTypeMap(HK_CATEGORIES);

const categories = computed(() => {
  if (!Array.isArray(availableCodes.value) || availableCodes.value.length === 0) {
    return [];
  }

  const categoryMap = new Map();

  availableCodes.value.forEach((dataTypeKey) => {
    const mapping = dataTypeMap.get(dataTypeKey);

    if (mapping) {
      const { categoryName, categoryLogo, dataType } = mapping;
      addToCategoryMap(categoryMap, categoryName, categoryLogo, dataType);
    }
  });

  return Array.from(categoryMap.values());
});

const codeToNameMap = new Map(
  Object.values(HK_CATEGORIES)
    .flatMap(({ dataType }) => dataType)
    .map(({ key, name }) => [key, name])
);

function humanizeHKCode(codeValue = '') {
  if (!codeValue) return '';

  let s = codeValue
    .replace(/^HKQuantityTypeIdentifier/, '')
    .replace(/^HKCategoryTypeIdentifier/, '')
    .replace(/^HKCategoryValue/, '')
    .replace(/^HKDataTypeIdentifier/, '')
    .replace(/^HKWorkoutTypeIdentifier/, 'Workout');

  s = s
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

  s = s
    .split(' ')
    .map((w) => (w.toUpperCase() === w ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');

  return s || codeValue;
}

function getCategoryName(codeValue) {
  return codeToNameMap.get(codeValue) || humanizeHKCode(codeValue) || '';
}

const formatCSVDataWithHealthKit = (data, patientId) => [
  COLUMNS_CSV_WITH_HEALTH_KIT_DATA,
  ...data.map(({ id: healthDataId, code, value, unit, startDate, device, endDate }) => {
    const codeValue = code?.codeValue || '';
    const categoryName = getCategoryName(codeValue);

    const isSleepAnalysis = HK_CATEGORIES_SLEEP_ANALYSIS.includes(codeValue);

    const dateCell = isSleepAnalysis
      ? `${formatDate(startDate)} - ${formatDate(endDate)}`
      : formatDate(startDate);

    const deviceForCSV = [device?.display, device?.type].filter(Boolean).join(' ').trim();

    const n = typeof value === 'number' ? value : Number(value);
    const valueCell = Number.isFinite(n) ? n.toFixed(2) : (value ?? '');

    return [patientId, healthDataId, categoryName, dateCell, valueCell, unit ?? '', deviceForCSV];
  }),
];

function pad2(n) {
  return String(n).padStart(2, '0');
}

function sanitizeFilePart(str) {
  return String(str || '')
    .trim()
    .replace(/[^a-zA-Z0-9-_]+/g, '_')
    .replace(/_+/g, '_');
}

function buildHealthDataFileName(patientId) {
  const now = new Date();
  const stamp = `${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}-${now.getFullYear()}`;

  const safePatientId = sanitizeFilePart(patientId);
  return `health_data_${stamp}_${safePatientId}`;
}

const downloadCSVWithHealthKitData = async () => {
  try {
    isDownloadingCSV.value = true;

    const patientId = collectedFormData.userId;

    const data = await getHealthKitData({
      studyId: collectedFormData.studyId,
      userId: collectedFormData.userId,
      withSort: true,
    });

    const csvData = formatCSVDataWithHealthKit(data, patientId);
    const fileName = buildHealthDataFileName(patientId);
    generateCSVFile(csvData, fileName);
  } catch (error) {
    toast.error('Error downloading CSV: ' + error.message);
  } finally {
    isDownloadingCSV.value = false;
  }
};

onMounted(async () => {
  try {
    isLoading.value = true;
    availableCodes.value = await getAvailableCategories({
      studyId: collectedFormData.studyId,
      userId: collectedFormData.userId,
    });
  } catch (error) {
    toast.error(error.message);
  } finally {
    isLoading.value = false;
  }
});
</script>

<style lang="scss" scoped>
.categories-grid {
  display: grid;
  gap: 20px 30px;
  grid-template-columns: repeat(2, minmax(300px, 1fr));
  justify-content: center;
  align-items: center;
}

.button-download {
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 140px;
  justify-content: center;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.spinner-circular {
  width: 14px;
  height: 14px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
